/** One price book item: what it is, its current price, and every price before it. */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, FileDown } from 'lucide-react';
import { ApiError, api, apiDownload } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { cost, staleLabel, validity, vatLabel, type Currency, type VatBasis } from '../../lib/pricebook';
import { HelpButton } from '../../components/HelpButton';
import { Alert, Card, Skeleton, StatusChip, TableWrap, Td, Th } from '../../components/ui';
import type { PriceRow } from './PriceBookPage';

interface HistoryRow {
  id: string;
  versionNo: number;
  cost: string;
  vatBasis: VatBasis;
  currency: Currency;
  validFrom: string | null;
  validUntil: string | null;
  priceListRef: string | null;
  sourceRef: string | null;
  sourceFile: { id: string; name: string } | null;
  source: string;
  createdAt: string;
  createdBy: string | null;
  validToday: boolean;
}

type Detail = PriceRow & { today: string; history: HistoryRow[] };

export function PriceBookItemPage(): JSX.Element {
  const { id } = useParams();
  const [fileError, setFileError] = useState<string | null>(null);
  const item = useQuery({
    queryKey: ['pricebook-item', id],
    queryFn: () => api<{ data: Detail }>(`/pricebook/items/${id}`),
    enabled: Boolean(id),
  });

  const download = async (file: { id: string; name: string }): Promise<void> => {
    setFileError(null);
    try {
      await apiDownload(`/pricebook/files/${file.id}`, file.name);
    } catch (caught) {
      setFileError(caught instanceof ApiError ? caught.message : 'Could not download the price list.');
    }
  };

  if (item.isLoading) return <Skeleton className="mx-auto h-64 max-w-5xl" />;
  if (item.isError || !item.data) {
    return (
      <div className="mx-auto max-w-5xl">
        <Alert tone="critical">Could not load this price item.</Alert>
      </div>
    );
  }

  const d = item.data.data;
  const badge = staleLabel(d.stale, d.staleReason);
  const facts: [string, string][] = [
    ['Supplier', `${d.supplier.name} (${d.supplier.code})`],
    ['Brand', d.brand ?? '—'],
    ['Part no.', d.partNo ?? '—'],
    ['Model', d.model ?? '—'],
    ['Category', d.category?.label ?? '—'],
    ['Unit', d.unit ?? '—'],
  ];

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <Link to="/suppliers/price-book" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:underline">
        <ArrowLeft aria-hidden="true" className="h-4 w-4" /> Price Book
      </Link>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-navy dark:text-white">{d.model ?? d.partNo}</h1>
          <p className="text-sm text-slate-500">{d.description ?? 'No description'}</p>
        </div>
        <HelpButton topic="pricebook" />
      </header>

      <div className="grid gap-5 md:grid-cols-3">
        <Card title="Current price" className="md:col-span-1">
          {d.current ? (
            <>
              <p className="tabular text-2xl font-bold text-navy dark:text-white">
                {cost(d.current.cost, d.current.currency)}
              </p>
              <p className="text-sm text-slate-500">
                {vatLabel(d.current.vatBasis)}
                {d.unit ? ` per ${d.unit}` : ''} · version {d.current.versionNo}
              </p>
              <p className="mt-2 text-sm">{validity(d.current.validFrom, d.current.validUntil)}</p>
              {badge && (
                <div className="mt-2">
                  <StatusChip tone={d.staleReason === 'NOT_YET_VALID' ? 'info' : 'warning'}>{badge}</StatusChip>
                  <p className="mt-1 text-xs text-slate-500">
                    No price is valid today. Confirm with the supplier before quoting.
                  </p>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-slate-500">No price recorded.</p>
          )}
        </Card>
        <Card title="Item" className="md:col-span-2">
          <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            {facts.map(([label, value]) => (
              <div key={label}>
                <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
                <dd className="text-slate-800 dark:text-slate-100">{value}</dd>
              </div>
            ))}
          </dl>
        </Card>
      </div>

      {fileError && <Alert tone="critical">{fileError}</Alert>}

      <Card title="Price history" description="Every price this supplier has quoted for the item, newest first. Nothing is overwritten.">
        <TableWrap>
          <thead>
            <tr>
              <Th>Version</Th>
              <Th align="right">Cost</Th>
              <Th>Valid</Th>
              <Th>Price list</Th>
              <Th>Imported</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {d.history.map((h) => (
              <tr key={h.id}>
                <Td>
                  v{h.versionNo}
                  {d.current?.versionNo === h.versionNo && (
                    <span className="ml-2">
                      <StatusChip tone={d.stale ? 'warning' : 'ok'}>{d.stale ? 'Latest' : 'Current'}</StatusChip>
                    </span>
                  )}
                </Td>
                <Td align="right" className="tabular whitespace-nowrap">
                  {cost(h.cost, h.currency)}
                  <span className="block text-xs text-slate-500">{vatLabel(h.vatBasis)}</span>
                </Td>
                <Td>
                  <span className="whitespace-nowrap text-xs">{validity(h.validFrom, h.validUntil)}</span>
                </Td>
                <Td>
                  <span className="text-xs">{h.priceListRef ?? '—'}</span>
                  {h.sourceFile && (
                    <button
                      type="button"
                      className="mt-1 flex items-center gap-1 text-xs font-medium text-brand hover:underline"
                      onClick={() => void download(h.sourceFile!)}
                    >
                      <FileDown aria-hidden="true" className="h-3.5 w-3.5" /> {h.sourceFile.name}
                    </button>
                  )}
                  {!h.sourceFile && h.sourceRef && <span className="block break-all text-xs text-slate-500">{h.sourceRef}</span>}
                </Td>
                <Td>
                  <span className="text-xs">{dateTime(h.createdAt)}</span>
                  <span className="block text-xs text-slate-500">
                    {h.createdBy ?? '—'}
                    {h.source === 'API' ? ' · API' : ''}
                  </span>
                </Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      </Card>
    </div>
  );
}
